import { LoadingOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Form, Input, Spin } from "antd";
import { ReactElement } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ruleRequired } from "../../../constants/rules/RulesConstantsForms";
import { ValidateFormErrorMessages } from "../../../constants/validateFormErrorMessages";
import { API_AUTH_AGENT } from "../../../store/slice/authSliceLogin/authSliceLogin";
import { useThunkActionLoadingState } from "../../../utils/Hooks/useThunkActionLoadingState";
import {
  handleBlurEmail,
  handleBlurPassword,
  handleChangeInputHideFormError,
} from "../helpers/agentLoginFormHelpers";
import { AgentLoginFieldType, IAgentLoginResponse } from "../types/types";

import classes from "./AgentLoginForm.module.css";

export function AgentLoginForm(): ReactElement {
  const navigate = useNavigate();
  const [form] = Form.useForm<AgentLoginFieldType>();

  const { isFetching, sendQuery } = useThunkActionLoadingState();

  async function onFinish(values: AgentLoginFieldType) {
    const transformValues = { ...values, email: values.email.toLowerCase() };
    const response = await sendQuery<IAgentLoginResponse>(
      API_AUTH_AGENT(transformValues),
    );
    if (response?.data?.success) navigate("/");
  }

  const submitBtnChild = isFetching ? (
    <Spin
      indicator={<LoadingOutlined className="loader-white" spin />}
      size="small"
    />
  ) : (
    "Войти"
  );

  function helpPasswordFormItem(errors: string[]) {
    return (
      <div className={classes.helpPasswordFormItem}>
        <div>{errors[0]}</div>
        <Link to="/recoveryPassword">Забыли пароль?</Link>
      </div>
    );
  }

  return (
    <ConfigProvider
      input={{
        className: classes.input,
      }}
    >
      <Form
        name="basic"
        onFinish={onFinish}
        form={form}
        className={classes.formContainer}
      >
        <div className={classes.mainFormContent}>
          <Form.Item
            rules={[
              ruleRequired,
              {
                type: "email",
                message: ValidateFormErrorMessages["enterCorrectEmail"],
              },
            ]}
            validateTrigger={false}
            name="email"
            className={classes.emailLoginFormItem}
          >
            <Input
              data-cy={"button-input-auth-email"}
              autoComplete="username"
              size="large"
              variant="filled"
              placeholder="Email"
              onBlur={handleBlurEmail(form)}
              onChange={handleChangeInputHideFormError(form, "email")}
              prefix={<MailOutlined style={{ color: "#6C6464" }} />}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldError }) => {
              return (
                <Form.Item
                  validateTrigger={false}
                  rules={[ruleRequired]}
                  className={classes.emailLoginFormItem}
                  name="password"
                  help={helpPasswordFormItem(getFieldError("password"))}
                >
                  <Input.Password
                    data-cy={"button-input-auth-password"}
                    size="large"
                    variant="filled"
                    placeholder="Пароль"
                    autoComplete="current-password"
                    onBlur={handleBlurPassword(form)}
                    onChange={handleChangeInputHideFormError(form, "password")}
                    prefix={<LockOutlined style={{ color: "#6C6464" }} />}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        <Button
          size="large"
          name="submitForm"
          className={classes.submitBtn}
          variant="solid"
          htmlType="submit"
          disabled={isFetching}
          color={"primary"}
        >
          {submitBtnChild}
        </Button>
      </Form>
    </ConfigProvider>
  );
}
